import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import { observer } from "mobx-react";
import { styles } from "../Styles";
import * as echarts from "echarts/core";
import {
  DatasetComponent,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  TransformComponent,
  MarkLineComponent,
} from "echarts/components";
import { LineChart } from "echarts/charts";
import { UniversalTransition } from "echarts/features";
import { CanvasRenderer } from "echarts/renderers";
import ReactEcharts from "echarts-for-react";

echarts.use([
  DatasetComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  TransformComponent,
  LineChart,
  CanvasRenderer,
  UniversalTransition,
]);

class AnalysisViewEchart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      monthDict: {
        1: "Jan",
        2: "Feb",
        3: "Mar",
        4: "Apr",
        5: "May",
        6: "Jun",
        7: "Jul",
        8: "Aug",
        9: "Sep",
        10: "Oct",
        11: "Nov",
        12: "Dec",
      },

      quarter: {
        0: "Q1",
        1: "Q2",
        2: "Q3",
        3: "Q4",
      },
    };
  }

  configureChart(baselineXPos, analysis, task) {
    const disp = [];
    const paddArray = [];

    baselineXPos.forEach((element) => {
      disp.push({
        type: "line",
        name: "Baseline",
        stack: "top",
        markLine: {
          symbol: "none",
          label: {
            show: false,
          },
          lineStyle: {
            color: "grey",
            type: "solid",
            width: 3,
          },
          data: [
            {
              name: "Baseline",
              xAxis: element,
            },
          ],
        },
      });
      disp.push({
        type: "line",
        name: "Baseline",
        stack: "top",
        markLine: {
          symbol: "none",
          label: {
            show: false,
          },
          lineStyle: {
            color: "grey",
            type: "solid",
            width: 3,
          },
          data: [
            {
              name: "Baseline",
              xAxis: element,
            },
          ],
        },
        xAxisIndex: 1,
        yAxisIndex: 1,
      });
    });

    let chartTitle;
    chartTitle =
      task === undefined || task.name === undefined
        ? " - Performance Indices vs Earned Value Analysis"
        : task.name + " - Performance Indices vs Earned Value Analysis";
    disp.push({
      type: "line",
      name: "Cost Performance Cumulative (CPI)",
      data: paddArray.concat(
        analysis.map((x) => {
          return x.costPerformanceIndex;
        })
      ),
      yAxisIndex: 0,
      showSymbol: false,
    });
    disp.push({
      type: "line",
      name: "Schedule Performance Cumulative (SPI($))",
      data: paddArray.concat(
        analysis.map((x) => {
          return x.schedulePerformanceIndexCost;
        })
      ),
      yAxisIndex: 0,
      showSymbol: false,
    });
    disp.push({
      type: "line",
      name: "Budget at Completion (BAC)",
      data: paddArray.concat(
        analysis.map((x) => {
          return x.budgetAtCompletion;
        })
      ),
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
    });
    disp.push({
      type: "line",
      name: "Forecast at Completion (FAC)",
      data: paddArray.concat(
        analysis.map((x) => {
          return x.forecastAtCompletionMean;
        })
      ),
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
    });
    disp.push({
      type: "line",
      name: "Planned Value (PV)",
      data: paddArray.concat(
        analysis.map((x) => {
          return x.plannedValue;
        })
      ),
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
    });
    disp.push({
      type: "line",
      name: "Earned Value (EV)",
      data: paddArray.concat(
        analysis.map((x) => {
          return x.earnedValue;
        })
      ),
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
    });
    disp.push({
      type: "line",
      name: "Actual Cost (AC)",
      data: paddArray.concat(
        analysis.map((x) => {
          return x.actualCost;
        })
      ),
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
    });
    disp.push({
      type: "line",
      name: "Forecasted Cost (FC)",
      data: paddArray.concat(
        analysis.map((x) => {
          return x.forecastedCost;
        })
      ),
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
    });

    return {
      data: disp,
      title: chartTitle,
      pad: paddArray,
    };
  }

  createChart(task, prebaselinePeriods) {
    const analysis =
      task === undefined || task.analysis === null ? [] : task.analysis;

    const startDate =
      task.plannedStartDate === undefined || task.plannedStartDate === null
        ? new Date()
        : new Date(task.plannedStartDate);

    let chartToolTip = [];
    let baselineXPos = [];

    const bottomAxis = analysis.map((x, index) => {
      let mm = String(((startDate.getMonth() + index) % 12) + 1);
      let yyyy = startDate.getFullYear();
      const mapping = parseInt(mm) / 3.1;
      chartToolTip.push(
        yyyy + String(" " + this.state.quarter[parseInt(mapping)])
      );

      if (mm === "12") {
        startDate.setYear(startDate.getFullYear() + 1);
      }

      let label =
        x.period + "\n(" + this.state.monthDict[mm] + " " + yyyy + ")";

      if (prebaselinePeriods.includes(x.period)) {
        baselineXPos.push(label);
      }

      return label;
    });

    const chartConfiguration = this.configureChart(
      baselineXPos,
      analysis,
      task
    );

    const data = chartConfiguration.data;
    const chartTitle = chartConfiguration.title;

    const generateTooltip = (param) => {
      return (
        chartToolTip[param.dataIndex] +
        "<br/>" +
        param.seriesName +
        ": " +
        Math.round(param.data)
      );
    };

    const options = {
      animationDuration: 1000,
      legend: {
        type: "scroll",
        width: "75%",
        bottom: 75,
        data: [
          "Cost Performance Cumulative (CPI)",
          "Schedule Performance Cumulative (SPI($))",
          "Budget at Completion (BAC)",
          "Forecast at Completion (FAC)",
          "Planned Value (PV)",
          "Earned Value (EV)",
          "Actual Cost (AC)",
          "Forecasted Cost (FC)",
          "Baseline",
        ],
      },
      title: {
        left: "center",
        text: chartTitle,
      },
      tooltip: {
        order: "valueDesc",
        trigger: "axis",
        formatter: function (params) {
          let ToolTip = "";
          params.forEach((param) => {
            ToolTip += "<br/>" + generateTooltip(param) + "<br/>";
          });
          return ToolTip;
        },
      },
      grid: [
        {
          left: 60,
          right: 50,
          height: "20%",
        },
        {
          left: 60,
          right: 50,
          top: "40%",
          height: "35%",
        },
      ],
      axisPointer: {
        link: [
          {
            xAxisIndex: [0, 1],
          },
        ],
      },
      xAxis: [
        {
          type: "category",
          axisLabel: {
            formatter: function (period) {
              return period;
            },
          },
          data: bottomAxis,
          boundaryGap: false,
        },
        {
          gridIndex: 1,
          type: "category",
          axisLabel: { show: false },
          data: bottomAxis,
          boundaryGap: false,
          axisLine: { onZero: true },
          show: false,
        },
      ],
      yAxis: [
        {
          type: "value",
          axisLabel: { show: false },
        },
        {
          gridIndex: 1,
          type: "value",
          axisLabel: { show: false },
        },
      ],
      dataZoom: [
        {
          bottom: 40,
          type: "slider",
          xAxisIndex: [0, 1],
          filterMode: "none",
        },
        {
          type: "slider",
          yAxisIndex: [0, 1],
          bottom: 125,
          height: 310,
          filterMode: "none",
        },
        {
          type: "inside",
          xAxisIndex: [0, 1],
          filterMode: "none",
        },
        {
          type: "inside",
          yAxisIndex: [0, 1],
          filterMode: "none",
        },
      ],
      series: data,
    };
    return options;
  }

  render() {
    const options = this.createChart(
      this.props.task,
      this.props.prebaselinePeriods
    );
    return <ReactEcharts style={{ height: 500 }} option={options} />;
  }
}

AnalysisViewEchart.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(observer(AnalysisViewEchart));
